import { useEffect, useState } from 'react';
import { Button, Card } from '@orion/ui';
import { useSettingsQuery, useUpdateSettings } from './api.js';

export interface SettingsField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'email' | 'select';
  help?: string;
  options?: Array<{ value: string; label: string }>;
}

interface SettingsFormProps {
  namespace: string;
  fields: SettingsField[];
}

/** One generic form drives every settings namespace (General, Branding,
 * Security, Email, Notifications, Data retention) — they're all just a flat
 * object validated server-side against the schema in
 * packages/contracts/src/admin/settings.ts, so one renderer covers all six
 * instead of six near-identical hand-written forms. */
export function SettingsForm({ namespace, fields }: SettingsFormProps) {
  const { data, isLoading } = useSettingsQuery<Record<string, unknown>>(namespace);
  const update = useUpdateSettings(namespace);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setValues(data);
  }, [data]);

  const setField = (key: string, value: unknown) => {
    setValues((v) => ({ ...v, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync(values);
      setDirty(false);
    } catch {
      setError('Some values were invalid — please check the fields below.');
    }
  };

  if (isLoading) return <p className="p-7 text-sm text-text-muted">Loading…</p>;

  return (
    <Card className="max-w-xl p-5">
      {error && (
        <div role="alert" className="mb-4 rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      <div className="flex flex-col gap-4">
        {fields.map((f) => (
          <label key={f.key} className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            {f.type === 'boolean' ? (
              <span className="flex items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  checked={Boolean(values[f.key])}
                  onChange={(e) => setField(f.key, e.target.checked)}
                  className="size-4"
                />
                {f.label}
              </span>
            ) : f.type === 'select' ? (
              <>
                {f.label}
                <select
                  value={(values[f.key] as string | undefined) ?? ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
                >
                  {(f.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                {f.label}
                <input
                  type={f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : 'text'}
                  value={(values[f.key] as string | number | undefined) ?? ''}
                  onChange={(e) => setField(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                  className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
                />
              </>
            )}
            {f.help && <span className="text-xs font-normal text-text-muted">{f.help}</span>}
          </label>
        ))}
      </div>

      {dirty && (
        <div className="mt-5 flex items-center justify-between rounded-control bg-night px-4 py-3 text-sm text-white">
          <span>You have unsaved changes</span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => data && setValues(data)}>
              Discard
            </Button>
            <Button size="sm" onClick={save} loading={update.isPending}>
              Save changes
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
